/* import __COLOCATED_TEMPLATE__ from './selector-control.hbs'; */
/**

## Selector Control

The Selector Control component is a set of controls with corresponding content blocks that allow switching
between the sections via the controls.

It supports 3 different UI
patterns: [Tabs](./selector-control/navigation/item/tab.html),
[Steps (Breadcrumbs)](./selector-control/navigation/item/step.html),
or [Segments](./selector-control/navigation/item/segment.html)

### Contextual Components

| Description | Contextual Component |
| --- | --- |
| Navigation | [selector-control/navigation](./selector-control/navigation.html) |
| Item | [selector-control/item](./selector-control/navigation/item.html) |
| Button | [selector-control/navigation/button](./selector-control/navigation/button.html) |
| Content | [selector-control/content](./selector-control/content.html) |
| Section | [selector-control/content/section](./selector-control/content/section.html) |

```example
@title Simple Tab Selector
@description Set up your selector-control and pass an array of `items` to it. These items should correspond
to the `name` on your nav items (if no name is passed, the `label` property will be used as a name instead).
These should also match the `item` properties passed to each of the content sections. The current item defaults
to the first item in the array.

@controller-properties {
  items: ['Triangle', 'Circle', 'Square']
}

@hbs
{{#selector-control items=items as |selector|}}
  {{#selector.navigation as |nav|}}
    {{nav.item label="Triangle"}}
    {{nav.item label="Circle"}}
    {{nav.item label="Square"}}
  {{/selector.navigation}}

  {{#selector.content as |content|}}
    {{#content.section key="Triangle"}}
      <p>Hello I am some triangle content</p>
    {{/content.section}}
    {{#content.section key="Circle"}}
      <p>Hi I'm a rad circle content</p>
    {{/content.section}}
    {{#content.section key="Square"}}
      <p>Yo, I'm a square</p>
    {{/content.section}}
  {{/selector.content}}
{{/selector-control}}

```

```example
@title Fancy Labels
@description It's recommended that you use the `name` property to pass the item an identifier instead of
relying on the fallback to the `label` property, especially with i18n. You can also pass a block instead
of using the `label` property if you want to include extra HTML for formatting

@controller-properties {
  items: ['triangle', 'circle', 'square']
}

@hbs
{{#selector-control type="SEGMENT" items=items as |selector|}}
  {{#selector.navigation as |nav|}}
    {{nav.item key="triangle" label="Triangle"}}
    {{nav.item key="circle" label="Circle"}}
    {{#nav.item key="square"}}
      Square <em>(Premium Shape)</em>
    {{/nav.item}}
  {{/selector.navigation}}

  {{#selector.content as |content|}}
    {{#content.section key="triangle"}}
      <p>Hello I am some triangle content</p>
    {{/content.section}}
    {{#content.section key="circle"}}
      <p>Hi I'm a rad circle content</p>
    {{/content.section}}
    {{#content.section key="square"}}
      <p>Yo, I'm a square</p>
    {{/content.section}}
  {{/selector.content}}
{{/selector-control}}

```

```example
@title Setting the Current Item
@description You can pass the `name` of the item you want to be initially selected to the selector control to have it
display as the active item.

@controller-properties {
  items: ['triangle', 'circle', 'square']
}

@hbs
{{#selector-control items=items currentItem="circle" as |selector|}}
  {{#selector.navigation as |nav|}}
    {{nav.item key="triangle" label="Triangle"}}
    {{nav.item key="circle" label="Circle"}}
    {{nav.item key="square" label="Square"}}
  {{/selector.navigation}}

  {{#selector.content as |content|}}
    {{#content.section key="triangle"}}
      <p>Hello I am some triangle content</p>
    {{/content.section}}
    {{#content.section key="circle"}}
      <p>Hi I'm a rad circle content</p>
    {{/content.section}}
    {{#content.section key="square"}}
      <p>Yo, I'm a square</p>
    {{/content.section}}
  {{/selector.content}}
{{/selector-control}}

```


```example
@title Segmented Control
@description You can choose between 3 different UIs for the selector control by passing a `type` to it. (TAB, STEP, or SEGMENT)

@controller-properties {
  items: ['triangle', 'circle', 'square']
}

@hbs
{{#selector-control type="SEGMENT" items=items as |selector|}}
  {{#selector.navigation as |nav|}}
    {{nav.item key="triangle" label="Triangle"}}
    {{nav.item key="circle" label="Circle"}}
    {{nav.item key="square" label="Square"}}
  {{/selector.navigation}}

  {{#selector.content as |content|}}
    {{#content.section key="triangle"}}
      <p>Hello I am some triangle content</p>
    {{/content.section}}
    {{#content.section key="circle"}}
      <p>Hi I'm a rad circle content</p>
    {{/content.section}}
    {{#content.section key="square"}}
      <p>Yo, I'm a square</p>
    {{/content.section}}
  {{/selector.content}}
{{/selector-control}}

```

```example
@title Stepped or Wizard Navigation

@description Stepped navigtion can be passed controls (next & previous) in order to navigate through the steps in order,
since an in-active step cannot be clicked on. The next and previous buttons will navigate forwards or backwards by default.

@controller-properties {
  items: ['triangle', 'circle', 'square']
}

@hbs
{{#selector-control type="STEP" items=items as |selector|}}
  {{#selector.navigation as |nav|}}
    {{nav.previous label="Previous"}}
    {{nav.item key="triangle" label="Triangle"}}
    {{nav.item key="circle" label="Circle"}}
    {{nav.item key="square" label="Square"}}
    {{nav.next label="Next"}}
  {{/selector.navigation}}

  {{#selector.content as |content|}}
     {{#content.section key="triangle"}}
      <p>Hello I am some triangle content</p>
    {{/content.section}}
    {{#content.section key="circle"}}
      <p>Hi I'm a rad circle content</p>
    {{/content.section}}
    {{#content.section key="square"}}
      <p>Yo, I'm a square</p>
    {{/content.section}}
  {{/selector.content}}
{{/selector-control}}
```

```example
@title Stepped Navigation with custom action

@description The next and previous buttons will navigate forwards or backwards by default. If you
wish to perform additional actions on these two controls, you can pass a custom action to the button
via the `onClick` handler.

@controller-properties
{
  items: ['triangle', 'circle', 'square'],
  actions: {
    myCustomNextAction() {
      alert('You clicked next!');
    }
  }
}

@hbs
{{#selector-control type="STEP" items=items as |selector|}}
  {{#selector.navigation as |nav|}}
    {{nav.previous label="Previous"}}
    {{nav.item key="triangle" label="Triangle"}}
    {{nav.item key="circle" label="Circle"}}
    {{nav.item key="square" label="Square"}}
    {{nav.next label="Next" onClick=(action "myCustomNextAction")}}
  {{/selector.navigation}}

  {{#selector.content as |content|}}
    {{#content.section key="triangle"}}
      <p>Hello I am some triangle content</p>
    {{/content.section}}
    {{#content.section key="circle"}}
      <p>Hi I'm a rad circle content</p>
    {{/content.section}}
    {{#content.section key="square"}}
      <p>Yo, I'm a square</p>
    {{/content.section}}
  {{/selector.content}}
{{/selector-control}}
```

### Parameters
@param {String} [type='TAB'] Determines the UI type of this element. 'TAB', 'STEP', or 'SEGMENT'
@param {String} [currentItem] Unique identifier (key or label) of the item that should be currently selected
@param {Array[String]} [items] An Array of item identifiers (keys or labels). These should map to the `name` and `section` parameters passed to individual items and content sections.
@param {Function} [didSelectItem=undefined] An optional function that will be triggered with an item when it is selected.
**/

import Component from '@ember/component';
import { computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import { assert } from '@ember/debug';
import { SELECTOR_TYPE } from './selector-control/navigation/item';
import { SELECTOR_NAV_ACTION_TYPE } from './selector-control/navigation/button';

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  tagName: '',
  // passed in array of item key strings
  items: null,
  currentItem: null,
  currentItemIndex: computed('currentItem', 'items.[]', function () {
    return this.get('items').indexOf(this.get('currentItem'));
  }),
  type: SELECTOR_TYPE.TAB,
  // This will only ever contain a single items for tabs and segments since the activeItem is
  // also the current item, but for steps it may be multiple items, as active steps include the
  // current step as well as previous steps
  activeItems: computed('currentItemIndex', 'items.[]', 'type', function () {
    const currentItemIndex = this.get('currentItemIndex');
    // If this is a step, then we need to populate the activeItems array with the
    // current step and the preceding steps
    if (this.get('type') === SELECTOR_TYPE.STEP) {
      return this.get('items').slice(0, currentItemIndex + 1);
      // Otherwise, we can just return an array with a single item — the item matching
      // the currentItemIndex position in the items array
    } else {
      return [this.get('items')[currentItemIndex]];
    }
  }),
  didReceiveAttrs() {
    this._super();

    // Assert that we've passed some items
    assert('You must pass an items array to {{selector-control}}', this.get('items').length > 0);

    // If we weren't passed a current item, set it to the first item
    if (!this.get('currentItem')) {
      this.set('currentItem', this.get('items')[0]);
    }
  },
  isFirstItem: equal('currentItemIndex', 0),
  isLastItem: computed('items.length', 'currentItemIndex', function () {
    return this.get('currentItemIndex') === this.get('items.length') - 1;
  }),
  setPreviousItem() {
    if (!this.get('isFirstItem')) {
      // We need to set the currentItem instead of decrementing the currentItemIndex
      // because the currentItemIndex is actually computed from the currentItem
      this.set('currentItem', this.get('items')[this.get('currentItemIndex') - 1]);
      this.get('didSelectItem')?.(this.currentItem);
    }
  },
  setNextItem() {
    if (!this.get('isLastItem')) {
      // We need to set the currentItem instead of incrementing the currentItemIndex
      // because the currentItemIndex is actually computed from the currentItem
      this.set('currentItem', this.get('items')[this.get('currentItemIndex') + 1]);
      this.get('didSelectItem')?.(this.currentItem);
    }
  },
  actions: {
    selectItem(item) {
      this.set('currentItem', item);
      this.get('didSelectItem')?.(item);
    },
    goToStep(direction) {
      switch (direction) {
        case SELECTOR_NAV_ACTION_TYPE.NEXT:
          this.setNextItem();
          break;
        case SELECTOR_NAV_ACTION_TYPE.PREVIOUS:
          this.setPreviousItem();
          break;
        default:
          assert(`Unknown direction: ${direction}`);
      }
    }
  }
});